import { useEffect, useRef } from 'react';
import { env } from '@/shared/lib/env';
import { getAccessToken } from '@/shared/lib/auth';
import type { ApiSupportMessage } from '@/shared/types/api/support';

/**
 * Shape the backend `ClientChatConsumer` sends over the socket on a new
 * message. See `apps/support/consumers.py::chat_message`:
 *   { "type": "message", "message": <MessageSerializer payload> }
 */
type ChatSocketEvent = {
  type: string;
  message?: ApiSupportMessage;
};

type UseChatSocketOptions = {
  /** Open the socket only when true (e.g. after the chat history loaded). */
  enabled?: boolean;
  /** Called with the new message on every `type: "message"` event. */
  onMessage: (message: ApiSupportMessage) => void;
  /** Called whenever a fresh connection is established (initial + reconnect). */
  onOpen?: () => void;
  /** Reports the live connection state for fallback polling decisions. */
  onConnectionChange?: (connected: boolean) => void;
};

const INITIAL_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;

/** Close codes the backend uses for an authentication failure; do not retry. */
const AUTH_CLOSE_CODES = new Set([4001, 4003]);

const buildSocketUrl = (token: string): string | null => {
  if (!env.apiUrl) return null;
  let origin: string;
  try {
    // env.apiUrl is e.g. https://api.tezcargojapan.uz/api/v1 — take the origin only.
    origin = new URL(env.apiUrl).origin;
  } catch {
    return null;
  }
  const wsOrigin = origin.replace(/^http/i, 'ws'); // http→ws, https→wss
  return `${wsOrigin}/ws/chat?token=${encodeURIComponent(token)}`;
};

/**
 * Opens a receive-only WebSocket to `/ws/chat` for realtime delivery of new
 * support-chat messages, replacing the old per-second polling.
 *
 * - Reconnects with exponential backoff (1s→2s→…→30s), reset on open.
 * - Closes cleanly on unmount; stops retrying on an auth close (4001/4003).
 * - Pauses while the tab is hidden and resumes (with a fresh sync) on focus.
 *
 * Sending stays on REST — this socket never sends.
 */
export const useChatSocket = ({
  enabled = true,
  onMessage,
  onOpen,
  onConnectionChange,
}: UseChatSocketOptions): void => {
  // Keep latest callbacks in refs so the effect doesn't reconnect on rerender.
  const onMessageRef = useRef(onMessage);
  const onOpenRef = useRef(onOpen);
  const onConnectionChangeRef = useRef(onConnectionChange);
  onMessageRef.current = onMessage;
  onOpenRef.current = onOpen;
  onConnectionChangeRef.current = onConnectionChange;

  useEffect(() => {
    if (!enabled) return;

    let socket: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let backoff = INITIAL_BACKOFF_MS;
    let stopped = false; // true on unmount or after an auth close

    const clearReconnect = (): void => {
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const scheduleReconnect = (): void => {
      if (stopped || reconnectTimer !== null) return;
      const delay = backoff;
      backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        connect();
      }, delay);
    };

    const connect = (): void => {
      if (stopped || document.visibilityState === 'hidden') return;

      const token = getAccessToken();
      const url = token ? buildSocketUrl(token) : null;
      if (!url) {
        scheduleReconnect();
        return;
      }

      const ws = new WebSocket(url);
      socket = ws;

      ws.onopen = () => {
        backoff = INITIAL_BACKOFF_MS;
        onConnectionChangeRef.current?.(true);
        onOpenRef.current?.();
      };

      ws.onmessage = (event) => {
        let data: ChatSocketEvent;
        try {
          data = JSON.parse(event.data) as ChatSocketEvent;
        } catch {
          return;
        }
        if (data.type === 'message' && data.message) {
          onMessageRef.current(data.message);
        }
      };

      ws.onclose = (event) => {
        onConnectionChangeRef.current?.(false);
        if (socket === ws) socket = null;
        if (AUTH_CLOSE_CODES.has(event.code)) {
          stopped = true;
          return;
        }
        scheduleReconnect();
      };

      ws.onerror = () => {
        // The matching `close` event handles reconnect scheduling.
        ws.close();
      };
    };

    const handleVisibility = (): void => {
      if (document.visibilityState === 'visible') {
        // Resume immediately on focus if we have no live/connecting socket.
        if (
          !stopped &&
          (socket === null || socket.readyState === WebSocket.CLOSED)
        ) {
          clearReconnect();
          backoff = INITIAL_BACKOFF_MS;
          connect();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    connect();

    return () => {
      stopped = true;
      clearReconnect();
      document.removeEventListener('visibilitychange', handleVisibility);
      onConnectionChangeRef.current?.(false);
      if (socket) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onclose = null;
        socket.onerror = null;
        socket.close();
        socket = null;
      }
    };
  }, [enabled]);
};
